import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Trophy, ChartLineUp, Robot, ArrowRight, Star, MapPin } from "@phosphor-icons/react";
import SEOHead from "@/lib/seo/SEOHead";

export default function PublicShowcase() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/agency/showcase/${slug}`);
        setData(data);
      } catch {}
      setLoading(false);
    })();
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><span className="spinner text-zinc-400"></span></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-8 text-center"><div><h1 className="font-display text-3xl font-black mb-2">Agency not found</h1><p className="text-zinc-600">Double check the URL and try again.</p></div></div>;

  const { agency, stats, leaderboard } = data;
  const color = agency.primary_color;

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      <SEOHead
        title={agency?.brand_name ? `${agency.brand_name} — Public Showcase` : "Agency Showcase | RankTrust"}
        description="Whitelabel agency public showcase powered by RankTrust."
        robots="noindex,follow"
        indexable={false}
      />
      {/* Hero */}
      <section className="border-b border-zinc-200 grid-bg">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <div className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-3">
              {agency.logo_url ? (
                <img src={agency.logo_url} alt={agency.brand_name} className="h-9 w-auto" />
              ) : (
                <span className="font-display font-black tracking-tight text-lg" style={{ color }}>{agency.brand_name}</span>
              )}
            </div>
            <div className="font-mono text-xs text-zinc-500">PUBLIC SHOWCASE</div>
          </div>

          <div className="label-tiny mb-4 flex items-center gap-2" style={{ color }}>
            <Trophy size={12} weight="fill" /> Client wins · Live leaderboard
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-black leading-[0.95] tracking-tighter max-w-4xl" data-testid="showcase-headline">
            {agency.brand_name} has put<br />
            <span style={{ background: color, color: "white", padding: "0 12px", display: "inline-block" }}>{stats.total_top_3_placements}</span>{" "}
            keywords in the<br />Top 3 of Google Maps.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-zinc-700 max-w-2xl leading-relaxed">{agency.tagline}</p>
        </div>

        {/* Stats strip */}
        <div className="border-t border-zinc-200 bg-zinc-50">
          <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-zinc-200">
            <Stat label="Active Clients" value={stats.total_clients} testid="showcase-stat-clients" />
            <Stat label="Top-3 Placements" value={stats.total_top_3_placements} testid="showcase-stat-top3" />
            <Stat label="Leads Captured (30d)" value={stats.total_leads_30d} testid="showcase-stat-leads" />
            <Stat label="Avg Lead Score" value={`${stats.avg_lead_capture_score}/100`} testid="showcase-stat-avg" />
          </div>
        </div>
      </section>

      {/* Leaderboard */}
      <section className="py-14">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="label-tiny mb-3" style={{ color }}>The Wins</div>
          <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter mb-8">Client Leaderboard</h2>

          {leaderboard.length === 0 ? (
            <div className="bento p-12 text-center">
              <Trophy size={28} className="mx-auto text-zinc-400 mb-3" />
              <p className="text-zinc-600">Wins will appear as soon as the first client snapshots are captured.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="showcase-leaderboard">
              {leaderboard.map((row, i) => (
                <div key={i} className="bento p-5 flex items-center gap-4 flex-wrap" data-testid={`showcase-row-${i}`}>
                  <div className="w-12 h-12 flex items-center justify-center font-display font-black text-2xl" style={{ background: color, color: "white" }}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-display font-bold text-lg">{row.name}</div>
                    <div className="font-mono text-xs text-zinc-500 flex items-center gap-2 mt-1">
                      <MapPin size={10} weight="bold" /> {row.city || "—"} · {row.category || ""}
                    </div>
                  </div>
                  <Pill icon={Star} label="Top-3" value={row.top_3_count ?? 0} delta={row.top_3_delta} accent={color} />
                  <Pill icon={ChartLineUp} label="Lead Score" value={row.lead_capture_score ?? "—"} delta={row.lead_capture_delta} accent={color} />
                  <Pill icon={Robot} label="ChatGPT" value={row.ai_visibility_rate != null ? `${row.ai_visibility_rate}%` : "—"} accent={color} />
                  <div className="font-mono text-xs text-zinc-500 hidden md:block">
                    <div>{row.leads_30d || 0} leads</div>
                    <div className="text-[10px]">last 30d</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-zinc-200" style={{ background: color, color: "white" }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-black tracking-tighter mb-4">Want to be on this leaderboard?</h2>
          <p className="text-lg opacity-90 mb-8">{agency.brand_name} is taking a limited number of new clients this quarter. Inquire below.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-white font-bold text-sm tracking-widest uppercase" style={{ color }} data-testid="showcase-cta">
            Get in touch <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </section>

      <footer className="py-6 text-center font-mono text-xs text-zinc-400 bg-zinc-50 border-t border-zinc-200 space-y-2">
        <div>Powered by <span className="text-zinc-600 font-semibold">RankTrust</span> · Data updated live from the platform</div>
        <div data-testid="footer-legal-entity" className="max-w-xl mx-auto px-4">
          RankTrust is a division of Card Service Warehouse, Inc., operating under the same legal entity and EIN.
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="py-6 px-5 first:pl-0" data-testid={testid}>
      <div className="font-display text-3xl md:text-4xl font-black tabular-nums">{value}</div>
      <div className="mt-1 text-xs text-zinc-600 font-mono uppercase tracking-widest">{label}</div>
    </div>
  );
}

function Pill({ icon: Icon, label, value, delta, accent }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-zinc-50 border border-zinc-200 min-w-[110px]">
      <Icon size={14} style={{ color: accent }} />
      <div>
        <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">{label}</div>
        <div className="font-display font-bold text-sm flex items-center gap-1.5 tabular-nums">
          {value}
          {delta != null && delta !== 0 && (
            <span className={`text-[10px] font-mono ${delta > 0 ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
              {delta > 0 ? "▲" : "▼"}{Math.abs(delta)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
